import React, { useState, useEffect, useCallback } from 'react'
import { Card, Select, Input, InputNumber, Table, Switch, Button, message, Spin, Typography, Tag, Empty } from 'antd'
import { GlobalOutlined, UserOutlined, SaveOutlined } from '@ant-design/icons'
import { backend } from '../../api'
import api from '../../api'

const { Title, Text } = Typography

const PROTOCOLS = [
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'socks4', label: 'SOCKS4' },
  { value: 'http', label: 'HTTP' },
  { value: 'https', label: 'HTTPS' }
]

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}
const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

function emptyProxy() {
  return { enabled: false, protocol: 'socks5', host: '', port: '', username: '', password: '', bypass: '<local>' }
}

function AccountCell({ record }) {
  const a = record
  const name = a.remark || a.nickname || PLATFORM_NAMES[a.platform] || '未命名'
  const color = PLATFORM_COLORS[a.platform] || '#999'
  const label = (PLATFORM_NAMES[a.platform] || '?')[0]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', overflow: 'hidden', border: '1px solid #e8e8e8', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} /> : <UserOutlined style={{ color: '#bbb', fontSize: 11 }} />}
        </div>
        <div style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, borderRadius: '50%', background: color, border: '1.5px solid #fff', fontSize: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>{label}</div>
      </div>
      <span style={{ fontSize: 12 }}>{name}</span>
    </div>
  )
}

export default function NetworkProxy() {
  const [global, setGlobal] = useState(emptyProxy())
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [cfgRes, accList] = await Promise.all([
        backend.getSettings(),
        api.getAccounts()
      ])
      const cfg = ((cfgRes && cfgRes.config) ? cfgRes.config : cfgRes) || {}
      setGlobal({ ...emptyProxy(), ...(cfg.globalProxy || {}) })
      const accs = (accList || []).map(a => ({
        ...a,
        key: a.account_id,
        proxy: { ...emptyProxy(), ...(a.proxy || {}) }
      }))
      setAccounts(accs)
    } catch (e) {
      message.error('加载设置失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const saveGlobal = async () => {
    setSaving(true)
    try {
      await backend.updateSettings({ globalProxy: global })
      message.success('全局代理已保存')
    } catch (e) {
      message.error('保存失败')
    } finally { setSaving(false) }
  }

  const updateAccountProxy = async (id, field, val) => {
    const idx = accounts.findIndex(a => a.account_id === id)
    if (idx === -1) return
    const acc = { ...accounts[idx] }
    acc.proxy = { ...acc.proxy, [field]: val }
    const newList = [...accounts]
    newList[idx] = acc
    setAccounts(newList)
  }

  const saveAccountProxy = async (id) => {
    const acc = accounts.find(a => a.account_id === id)
    if (!acc) return
    setSaving(true)
    try {
      await api.updateAccountProxy(id, acc.proxy)
      message.success(`${acc.remark || acc.nickname || '账号'} 代理已保存`)
    } catch (e) {
      message.error('保存失败')
    } finally { setSaving(false) }
  }

  const col = (title, key, render, width) => ({ title, key, dataIndex: key, width: width || 100, align: 'center', render })

  const columns = [
    { title: '账号', key: 'account', width: 150, fixed: 'left', render: (_, r) => <AccountCell record={r} /> },
    {
      title: '启用代理', key: 'enabled', width: 80, align: 'center',
      render: (_, r) => <Switch size="small" checked={!!r.proxy.enabled} onChange={v => updateAccountProxy(r.account_id, 'enabled', v)} />
    },
    {
      title: '协议', key: 'protocol', width: 90,
      render: (_, r) => <Select size="small" value={r.proxy.protocol || 'socks5'} onChange={v => updateAccountProxy(r.account_id, 'protocol', v)} options={PROTOCOLS} style={{ width: '100%' }} disabled={!r.proxy.enabled} />
    },
    {
      title: '主机', key: 'host', width: 130,
      render: (_, r) => <Input size="small" value={r.proxy.host || ''} onChange={e => updateAccountProxy(r.account_id, 'host', e.target.value)} placeholder="127.0.0.1" disabled={!r.proxy.enabled} />
    },
    {
      title: '端口', key: 'port', width: 80,
      render: (_, r) => <InputNumber size="small" value={r.proxy.port || ''} onChange={v => updateAccountProxy(r.account_id, 'port', v)} placeholder="1080" style={{ width: '100%' }} disabled={!r.proxy.enabled} />
    },
    {
      title: '用户名', key: 'username', width: 100,
      render: (_, r) => <Input size="small" value={r.proxy.username || ''} onChange={e => updateAccountProxy(r.account_id, 'username', e.target.value)} disabled={!r.proxy.enabled} />
    },
    {
      title: '密码', key: 'password', width: 100,
      render: (_, r) => <Input.Password size="small" value={r.proxy.password || ''} onChange={e => updateAccountProxy(r.account_id, 'password', e.target.value)} disabled={!r.proxy.enabled} />
    },
    {
      title: '操作', key: 'action', width: 70, fixed: 'right',
      render: (_, r) => <Button size="small" type="link" icon={<SaveOutlined />} onClick={() => saveAccountProxy(r.account_id)} loading={saving}>保存</Button>
    }
  ]

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title={<span><GlobalOutlined /> 全局代理</span>} extra={<Text type="secondary" style={{ fontSize: 12 }}>所有未单独设置代理的账号将使用此配置</Text>}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select value={global.protocol || 'socks5'} onChange={v => setGlobal({ ...global, protocol: v })} options={PROTOCOLS} style={{ width: 110 }} />
            <Input value={global.host || ''} onChange={e => setGlobal({ ...global, host: e.target.value })} placeholder="主机地址" style={{ width: 150 }} />
            <InputNumber value={global.port || ''} onChange={v => setGlobal({ ...global, port: v })} placeholder="端口" style={{ width: 100 }} />
            <Input value={global.username || ''} onChange={e => setGlobal({ ...global, username: e.target.value })} placeholder="用户名(选填)" style={{ width: 130 }} />
            <Input.Password value={global.password || ''} onChange={e => setGlobal({ ...global, password: e.target.value })} placeholder="密码(选填)" style={{ width: 130 }} />
            <Input value={global.bypass || '<local>'} onChange={e => setGlobal({ ...global, bypass: e.target.value })} placeholder="绕过规则" style={{ width: 140 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={saveGlobal} loading={saving}>保存全局代理</Button>
          </div>
        </Card>

        <Card title={<span><UserOutlined /> 各账号独立代理</span>} extra={<Text type="secondary" style={{ fontSize: 12 }}>开启后优先使用账号代理，未开启的沿用全局代理</Text>}>
          {accounts.length > 0 ? (
            <Table dataSource={accounts} columns={columns} rowKey="key" size="small" pagination={false} scroll={{ x: 950 }} />
          ) : (
            <Empty description="暂无账号，请先添加账号" />
          )}
        </Card>
      </div>
    </Spin>
  )
}
